import React from 'react';
import URL from 'url-parse';

import type { ChestAsset, ConfigurableChestProps } from '@swf/core/types/ChestInventory';
import { AssetType, ChestType } from '@swf/core/types/ChestInventory';

import { legacyChestSkinMap, starsRewardsChestSkinMap } from './../assets/chests';
import { legacyChestLogoMap } from './../assets/logos';
import styles from './ConfigurableChest.scss';

const getChest2AssetUrl = (
  assetType: AssetType,
  assets: ChestAsset[],
  starsCrmCdnBaseUrl?: string
) => {
  const asset = assets.find((x: ChestAsset) => x.type === assetType);
  if (asset && starsCrmCdnBaseUrl) {
    const { path } = JSON.parse(asset.uriHint);
    const url = new URL(starsCrmCdnBaseUrl);
    url.set('pathname', url.pathname + path);
    return url.toString();
  } else {
    return assetType === AssetType.Logo ? legacyChestLogoMap.default : legacyChestSkinMap.default;
  }
};

const getAssetsUrls = (
  { assets, logoName, chestType, customSkin, tierId }: ConfigurableChestProps,
  starsCrmCdnBaseUrl?: string
) => {
  let logoUrl = '';
  let skinUrl = '';
  if (chestType === ChestType.MultiPurposeChest) {
    // follow Chest 2.0 Roll Out Plan -> Logic flow for 1.0/2.0 Logos and Chests
    // https://confluence.pyrsoftware.ca/confluence/display/MTECH/Chest+2.0+Roll+Out+Plan
    if (assets?.length) {
      logoUrl = getChest2AssetUrl(AssetType.Logo, assets, starsCrmCdnBaseUrl);
      skinUrl = getChest2AssetUrl(AssetType.ChestSkin, assets, starsCrmCdnBaseUrl);
    } else {
      logoUrl = legacyChestLogoMap[logoName as keyof typeof legacyChestLogoMap];
      skinUrl =
        legacyChestSkinMap[customSkin as keyof typeof legacyChestSkinMap] ||
        legacyChestSkinMap.default;
    }
  } else if (
    chestType === ChestType.StarsRewards ||
    chestType === ChestType.StarsRewardsCrossVerticalLoyality
  ) {
    skinUrl =
      starsRewardsChestSkinMap[
        `${chestType}Tier${tierId}` as keyof typeof starsRewardsChestSkinMap
      ];
  } else {
    logoUrl = legacyChestLogoMap.default;
    skinUrl = legacyChestSkinMap.default;
  }

  return { logoUrl, skinUrl };
};

type Props = ConfigurableChestProps & {
  starsCrmCdnBaseUrl?: string;
};

const ConfigurableChest: React.FC<Props> = (props) => {
  const { logoUrl, skinUrl } = getAssetsUrls(props, props.starsCrmCdnBaseUrl);

  const logoStyle = { backgroundImage: `url("${logoUrl}")` };
  const skinStyle = { backgroundImage: `url("${skinUrl}")` };

  return (
    <div className={styles.container}>
      <div className={styles.top} style={skinStyle} />
      <div className={styles.light} style={skinStyle} />
      <div className={styles.body} style={skinStyle} />
      <div className={styles.locker} style={skinStyle} />
      <div className={styles.logo} style={logoStyle} />
    </div>
  );
};

export default ConfigurableChest;
